import { Audit } from "@/interfaces/Company";
import {
  AuditDTO,
  AuditQuestions,
  AuditQuestionsGroup,
  AuditRequirementDTO,
  ChecklistRequirementItem,
  ChecklistQuestionItem,
} from "@/interfaces/Audit";
import { Notification } from "@/interfaces/Notification";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const initialState = {
  questions: [] as AuditQuestions[],
  questionsGrouped: [] as AuditQuestionsGroup[],
  percentageObtained: 0 as number,
  totalVariableValue: 0 as number,
  totalValueObtained: 0 as number,
  percentageCompleted: 0 as number,
  auditData: [] as AuditDTO[],
  auditRequirementData: [] as AuditRequirementDTO[],
  observation: {} as {
    [req_id: number]: string | null;
  },
  audit: null as Audit | null,
  audit_notifications: [] as Notification[],
};

export const auditSlice = createSlice({
  name: "audit",
  initialState,
  reducers: {
    setAudit: (state, action: PayloadAction<Audit>) => {
      state.audit = action.payload;
    },
    removeAudit: (state) => {
      state.audit = null;
    },
    setAuditNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.audit_notifications = action.payload;
    },
    setAuditNotification: (state, action: PayloadAction<Notification>) => {
      const notificationExists = state.audit_notifications.find(
        (notification) => notification.id === action.payload.id,
      );

      if (!notificationExists) {
        state.audit_notifications.push(action.payload);
      }
    },
    setQuestions: (state, action: PayloadAction<AuditQuestions[]>) => {
      state.questions = action.payload;
    },
    setQuestionsGrouped: (
      state,
      action: PayloadAction<AuditQuestionsGroup[]>,
    ) => {
      state.questionsGrouped = action.payload;

      // state.totalVariableValue = calculateTotalVariableValue(action.payload);
      // state.percentageCompleted = calculatePercentageCompleted(
      //   state.totalValueObtained,
      //   state.totalVariableValue
      // );
    },
    setUpdateObservation: (
      state,
      action: PayloadAction<{ reqId: number; observation: string | null }>,
    ) => {
      const { reqId, observation } = action.payload;
      const requirement = findRequirementById(state.questionsGrouped, reqId);
      if (requirement) {
        const requirementIndex = state.auditRequirementData.findIndex(
          (data) => data.requirement == reqId,
        );

        if (requirementIndex >= 0) {
          state.auditRequirementData[requirementIndex] = {
            ...state.auditRequirementData[requirementIndex],
            observation,
          };
        }
      }
    },
    setUpdateRequirement: (
      state,
      action: PayloadAction<{
        requirementId: number;
        compliance: number;
        observation: string | null;
      }>,
    ) => {
      const { requirementId, compliance, observation } = action.payload;
      const requirement = findRequirementById(
        state.questionsGrouped,
        requirementId,
      );

      if (requirement) {
        const requirementIndex = state.auditRequirementData.findIndex(
          (data) => data.requirement == requirementId,
        );
        if (requirementIndex >= 0) {
          state.auditRequirementData[requirementIndex] = {
            ...state.auditRequirementData[requirementIndex],
            compliance,
            observation,
          };
        } else {
          state.auditRequirementData.push({
            compliance,
            observation,
            requirement: requirement.id,
          });
        }
      }
    },
    setCheckAuditDataForChanges: (state) => {
      state.auditData = state.auditData.filter((data) =>
        state.questionsGrouped.some((grouped) =>
          grouped.questions.some((question) => question.id === data.question),
        ),
      );
    },
    setUpdatePercentage: (
      state,
      action: PayloadAction<{
        questionId: number;
        compliance: number;
      }>,
    ) => {
      state.totalVariableValue = calculateTotalVariableValue(
        state.questionsGrouped,
      );
      const { questionId, compliance } = action.payload;
      // Filtrar auditData para eliminar preguntas que ya no están en questionsGrouped

      const question = findQuestionById(state.questionsGrouped, questionId);
      if (question) {
        const auditIndex = state.auditData.findIndex(
          (data) => data.question === questionId,
          //  && data.company === companyId
        );
        let obtainedValue = 0;
        if (auditIndex >= 0) {
          obtainedValue = calculateValueObtained(
            question.variable_value,
            compliance,
            state.auditData[auditIndex].is_articuled,
          );
          state.auditData[auditIndex] = {
            ...state.auditData[auditIndex],
            compliance,
            obtained_value: obtainedValue,
          };
        } else {
          obtainedValue = calculateValueObtained(
            question.variable_value,
            compliance,
            true, // Por defecto isArticulated es true
          );
          state.auditData.push({
            question: questionId,
            compliance,
            obtained_value: obtainedValue,
            verify_document: null,
            observation: null,
            is_articuled: true,
          });
        }

        state.totalValueObtained = calculateTotalValueObtainedFromAuditData(
          state.auditData,
        );
        // state.totalValueObtained += obtainedValue;
        state.percentageCompleted = calculatePercentageCompleted(
          state.totalValueObtained,
          state.totalVariableValue,
        );
      }
    },
    setUpdateQuestionObservation: (
      state,
      action: PayloadAction<{ questionId: number; observation: string }>,
    ) => {
      const { questionId, observation } = action.payload;
      const auditIndex = state.auditData.findIndex(
        (data) => data.question === questionId,
      );
      if (auditIndex >= 0) {
        state.auditData[auditIndex] = {
          ...state.auditData[auditIndex],
          observation,
        };
      }
    },
    setUpdateArticulated: (
      state,
      action: PayloadAction<{ questionId: number; isArticulated: boolean }>,
    ) => {
      const { questionId, isArticulated } = action.payload;
      const question = findQuestionById(state.questionsGrouped, questionId);
      if (question) {
        const auditIndex = state.auditData.findIndex(
          (data) => data.question === questionId,
        );
        if (auditIndex >= 0) {
          state.auditData[auditIndex] = {
            ...state.auditData[auditIndex],
            is_articuled: isArticulated,
            obtained_value: calculateValueObtained(
              question.variable_value,
              state.auditData[auditIndex].compliance,
              isArticulated,
            ),
          };

          state.totalValueObtained = calculateTotalValueObtainedFromAuditData(
            state.auditData,
          );
          // state.totalValueObtained += obtainedValue;
          state.percentageCompleted = calculatePercentageCompleted(
            state.totalValueObtained,
            state.totalVariableValue,
          );
        }
      }
    },
    resetAudit: (state) => {
      state.auditData.map((data) => (data.compliance = 2));
    },
    fullResetAudit: (state) => {
      state.questions = initialState.questions;
      state.questionsGrouped = initialState.questionsGrouped;
      state.percentageObtained = initialState.percentageObtained;
      state.totalVariableValue = initialState.totalVariableValue;
      state.totalValueObtained = initialState.totalValueObtained;
      state.percentageCompleted = initialState.percentageCompleted;
      state.auditData = initialState.auditData;
      state.auditRequirementData = initialState.auditRequirementData;
      state.observation = initialState.observation;
    },
    syncChecklistIds: (
      state,
      action: PayloadAction<{
        checklist_requirements: ChecklistRequirementItem[];
        checklist_questions: ChecklistQuestionItem[];
      }>,
    ) => {
      const { checklist_requirements, checklist_questions } = action.payload;

      // Mapear requirement_id -> checklist_id
      const reqIdMap = new Map<number, number>();
      checklist_requirements.forEach((item) => {
        reqIdMap.set(item.requirement_id, item.checklist_id);
      });

      // Mapear question_id -> checklist_id
      const qIdMap = new Map<number, number>();
      checklist_questions.forEach((item) => {
        qIdMap.set(item.question_id, item.checklist_id);
      });

      // Actualizar auditRequirementData: reemplazar requirement master por checklist_id
      state.auditRequirementData = state.auditRequirementData.map((item) => {
        const checklistId = reqIdMap.get(item.requirement);
        if (checklistId) {
          return { ...item, requirement: checklistId };
        }
        return item;
      });

      //// Actualizar auditData: reemplazar question master por checklist_id
      //state.auditData = state.auditData.map((item) => {
      //  const checklistId = qIdMap.get(item.question);
      //  if (checklistId) {
      //    return { ...item, question: checklistId };
      //  }
      //  return item;
      //});
    },
    setCheckAuditRequirementDataForChanges: (state) => {
      state.auditRequirementData = state.auditRequirementData.filter((data) =>
        state.questionsGrouped.some((group) => group.id === data.requirement),
      );
    },
  },
});
const calculateTotalVariableValue = (
  questionsGrouped: AuditQuestionsGroup[],
): number => {
  return questionsGrouped.reduce((total, group) => {
    return (
      total +
      group.questions.reduce((groupTotal, question) => {
        return groupTotal + question.variable_value;
      }, 0)
    );
  }, 0);
};

const calculateTotalValueObtainedFromAuditData = (
  auditData: AuditDTO[],
): number => {
  return auditData.reduce((total, data) => {
    // //(\"data.obtained_value\", data.obtained_value);
    // //(\"data.compliance\", data.compliance);
    return total + data.obtained_value;
  }, 0);
};

const calculateValueObtained = (
  variableValue: number,
  compliance: number,
  isArticulated?: boolean,
): number => {
  if (!isArticulated) {
    return variableValue;
  }
  switch (compliance) {
    case 1:
    case 4:
      return variableValue;
    case 2:
      return 0;
    case 3:
      return variableValue / 2;
    default:
      return 0;
  }
};

const calculatePercentageCompleted = (
  totalValueObtained: number,
  totalVariableValue: number,
): number => {
  if (totalVariableValue === 0) {
    return 0;
  }
  const percentage = (totalValueObtained / totalVariableValue) * 100;
  return parseFloat(percentage.toFixed(2));
};

const findQuestionById = (
  questionsGrouped: AuditQuestionsGroup[],
  id: number,
): AuditQuestions | undefined => {
  for (let group of questionsGrouped) {
    const question = group.questions.find((q) => q.id === id);
    if (question) {
      return question;
    }
  }
  return undefined;
};

const findRequirementById = (
  questionsGrouped: AuditQuestionsGroup[],
  id: number,
): AuditQuestionsGroup | undefined => {
  const requirement = questionsGrouped.find((qG) => qG.id === id);
  if (requirement) {
    return requirement;
  }
  return undefined;
};

export const {
  setQuestions,
  setQuestionsGrouped,
  setUpdatePercentage,
  setUpdateArticulated,
  resetAudit,
  setUpdateRequirement,
  setUpdateObservation,
  setAudit,
  removeAudit,
  setCheckAuditDataForChanges,
  setAuditNotifications,
  setAuditNotification,
  fullResetAudit,
  setUpdateQuestionObservation,
  syncChecklistIds,
  setCheckAuditRequirementDataForChanges,
} = auditSlice.actions;

export default auditSlice.reducer;
